import type { ReactNode } from 'react';
import { cx } from 'class-variance-authority';
import styles from './breadcrumb.module.scss';

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export type BreadcrumbLinkProps = {
  className: string;
  href: string;
  label: string;
};

type BreadcrumbProps = {
  items: BreadcrumbItem[];
  itemClassName?: string;
  // Render-prop so this package stays framework-agnostic — Next.js consumers
  // pass `next/link`; without it we fall back to a plain `<a>`.
  renderLinkComponent?: (props: BreadcrumbLinkProps) => ReactNode;
};

type RenderItemArgs = {
  item: BreadcrumbItem;
  itemClassName?: string;
  renderLinkComponent?: (props: BreadcrumbLinkProps) => ReactNode;
};

const renderBreadcrumbItem = ({
  item,
  itemClassName,
  renderLinkComponent
}: RenderItemArgs) => {
  const className = cx(styles.breadcrumbItem, itemClassName);

  if (!item.href) {
    return <span className={className}>{item.label}</span>;
  }

  if (renderLinkComponent) {
    return renderLinkComponent({
      className,
      href: item.href,
      label: item.label
    });
  }

  return (
    <a className={className} href={item.href}>
      {item.label}
    </a>
  );
};

const Breadcrumb = ({
  items,
  itemClassName,
  renderLinkComponent
}: BreadcrumbProps) => {
  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <span
            key={`${idx}-${item.label}`}
            className={styles.breadcrumbWrapper}
          >
            {renderBreadcrumbItem({
              item,
              itemClassName,
              renderLinkComponent
            })}
            {!isLast && (
              <span className={cx(styles.breadcrumbSeparator, itemClassName)}>
                {'>'}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;
